// EditUsername.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Modal,
  Button,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { useUserProfile } from "hooks/use-user-profile.hook";

const EditUsername = ({ editUsername, onClose }) => {
  const [errorText, setErrorText] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const navbarBackground = theme.palette?.navbar?.main;

  const { getUserDetails, checkUniqueUsername, updateProfile } =
    useUserProfile(user);

  useEffect(() => {
    loadUserData();
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "100vw", sm: "50vw", md: "35vw" },
    bgcolor: "background.paper",
    boxShadow: 0,
    borderRadius: 2,
  };

  const loadUserData = async () => {
    try {
      const data = await getUserDetails();
      setUsername(data.userName);
    } catch (error) {
      console.error("Error fetching user details: ", error);
      toast.error("Failed to fetch user details.");
    }
  };

  const validateUsername = (name) => {
    const validPattern = /^[a-zA-Z0-9-._]+$/;
    if (name.length < 5) {
      setErrorText("The username must be at least 5 characters long.");
      return false;
    }
    if (name.length > 30) {
      setErrorText("The username must be no more than 30 characters.");
      return false;
    }
    if (!name || !validPattern.test(name)) {
      setErrorText(
        "The username can only include letters, numbers, hyphens (`-`), periods (`.`), and underscores (`_`)."
      );
      return false;
    }
    setErrorText("");
    return true;
  };

  const handleUsernameChange = (e) => {
    const newName = e.target.value;
    setUsername(newName);
    validateUsername(newName);
  };

  const handleUpdateProfile = async () => {
    if (!validateUsername(username)) return;
    setLoading(true);
    try {
      await checkUniqueUsername(username);
      await updateProfile(username, setErrorText);
      const updatedUser = { ...user, userName: username };
      if (sessionStorage.getItem("user")) {
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
      } else {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
      toast.success("Username updated successfully.");
      loadUserData();
      onClose();
    } catch (e) {
      toast.error("Failed to update username");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={editUsername}
      onClose={onClose}
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
      }}
    >
      <Box sx={modalStyle}>
        <Box sx={{ p: 4 }}>
          <Typography
            variant="h4"
            component="div"
            color="#406da4"
            fontWeight="600"
          >
            Change Username
          </Typography>
          <Typography mt={1}>
            Want to change your username to something more personalized? Create
            a new username below. Please do <Typography component="span" fontWeight="bold">not</Typography> include
            any personally identifiable information (e.g. first name, last name,
            etc.). Usernames must be unique, free of profanity, and can only
            contain letters, numbers, hyphens (`-`), periods (`.`), and
            underscores (`_`).
          </Typography>
          <TextField
            sx={{ width: "100%", mt: 2, mb: 1 }}
            value={username}
            onChange={handleUsernameChange}
            placeholder="Username"
            error={Boolean(errorText)}
            helperText={errorText}
          />
          <Typography mt={1}>
            Usernames must be at least 5 characters long and no more than 30
            characters.
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            backgroundColor: navbarBackground,
            display: "flex",
            justifyContent: "space-between",
            gap: 5,
            px: 5,
            py: 3,
            borderRadius: "0 0 20px 20px",
          }}
        >
          <Button onClick={onClose} variant="outlined" sx={{ width: "45%" }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateProfile}
            disabled={Boolean(errorText) || username === user?.userName || loading}
            sx={{ width: "45%", display: "flex", gap: 1 }}
          >
            {loading && <CircularProgress size={20} />}
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditUsername;
